"use client";

import clsx from "clsx";
import { OrbIDCredential } from "@/components/identity/OrbIDCredential";
import { NetworkVignette } from "@/components/web/NetworkVignette";
import type { GraphState } from "@/components/web/types";
import { advanceScreen } from "@/lib/presenter/advanceScreen";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 1.6 — OrbID materialisation + first-proposal capture.
 *
 *   Phase 1 (~3 s): the unified credential card composes from five
 *   sub-credentials — LEI digits stream in, Corp DID anchors, Live
 *   Verification ref chip, KYB attestation refs stack, Verified Trader
 *   badge mints atop.
 *
 *   Phase 2: a short post-mint screen showing Al Madar drafting his first
 *   proposal back to Aurora — 28 tonnes Halal whole chicken, CIF Jebel Ali,
 *   USD 850K — and tapping Submit. (Atlas: "Proposal signed by your Corp
 *   DID and dispatched.")
 *
 * Split-screen handshake #2 fires at proposal submission (left pane: Aurora
 * receives the inbound proposal; right pane: Al Madar's "sent · awaiting"
 * state). The handshake itself is rendered by SplitScreen elsewhere; this
 * screen renders Phase 1 + 2 sequentially based on `phase`.
 */
export function OrbIdMaterialisation({
  phase = "credential",
  graph,
  className,
}: {
  phase?: "credential" | "proposal" | "submitted";
  graph?: GraphState;
  className?: string;
}) {
  const t = useTranslations("t1Registration");
  return (
    <div className={clsx("flex h-full min-h-0 flex-col gap-3 overflow-y-auto", className)}>
      {/* Credential (hero) on the left, email-client mock on the right so
       *  the audience sees BOTH the OrbID materialising AND the receipt-
       *  style notifications arriving — the magic moment + the
       *  reassurance that Orbitra handles back-office signalling. */}
      <div className="grid grid-cols-[2fr_1fr] gap-4">
        <OrbIDCredential
          legalName="Al Madar Foods LLC"
          jurisdiction="UAE"
          orbid="orbid:5493-almadar-ae"
          lei="0000-DEMO-ALMADAR-AE"
          corpDid="did:orb:orbid:5493-almadar-ae"
          liveVerificationRef="lvc_a4f7c91d"
          kybAttestationRefs={["kyb_tradelicense_a1", "kyb_coi_a1", "kyb_ubo_a1", "kyb_aml_a1"]}
          verifiedTraderTier="Verified"
        />
        <EmailInbox phase={phase} />
      </div>

      {phase !== "credential" && (
        <div className="glass p-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("orbidFirstActionTitle")}</p>
          <h3 className="mt-1 font-display text-base font-semibold text-orbitra-paper">{t("orbidFirstActionSub")}</h3>

          <div className="mt-3 grid grid-cols-2 gap-3 text-[11px]">
            <Field label={t("orbidFieldCommodity")}       value={t("orbidCommodityValue")} />
            <Field label={t("orbidFieldQuantity")}        value={t("orbidQuantityValue")} />
            <Field label={t("orbidFieldIncoterms")}       value={t("orbidIncotermsValue")} />
            <Field label={t("orbidFieldPrice")}           value={t("orbidPriceValue")} />
            <Field label={t("orbidFieldDeliveryWindow")}  value={t("orbidDeliveryWindowValue")} />
            <Field label={t("orbidFieldSignature")}       value="did:orb:corbid:5493-almadar-ae" mono />
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={advanceScreen}
              className={clsx(
                "rounded-md px-4 py-2 font-display text-[12px] font-semibold transition-state",
                phase === "submitted"
                  ? "bg-policy-pass text-orbitra-ink"
                  : "bg-state-verified text-orbitra-ink shadow-[0_0_18px_var(--state-verified-glow)]",
              )}
            >
              {phase === "submitted" ? t("orbidCtaSent") : t("orbidCtaSubmit")}
            </button>
            {phase === "submitted" && (
              <span className="font-mono text-[10px] text-policy-pass">
                {t("orbidStatusSubmitted")}
              </span>
            )}
          </div>
        </div>
      )}

      {graph && <NetworkVignette graph={graph} preset="orbid-mint" />}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className={clsx("mt-0.5 text-orbitra-paper", mono && "font-mono break-all")}>{value}</p>
    </div>
  );
}

/**
 * Inbox companion shown alongside the materialising OrbID credential.
 *   - During "credential" phase: only the OrbID-issued confirmation email
 *     is visible (it arrives together with the credential card painting in).
 *   - Once the user advances to "proposal" or "submitted": a second email
 *     arrives prompting the first action.
 * Visual styling intentionally evokes the WhatsApp mock at screen 0.3 —
 * Orbitra notifying the user through their existing channels, not yet
 * another in-app screen.
 */
function EmailInbox({ phase }: { phase: "credential" | "proposal" | "submitted" }) {
  const t = useTranslations("t1Registration");
  const showProposalEmail = phase !== "credential";
  return (
    <div className="glass flex flex-col overflow-hidden p-0">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-state-verified/15 bg-orbitra-ink/40 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="grid h-5 w-5 place-items-center rounded-full bg-state-verified/20 text-[9px] font-bold text-state-verified">✉</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-state-verified">{t("orbidInboxLabel")}</span>
        </div>
        <span className="font-mono text-[9px] text-orbitra-muted" dir="ltr">{t("orbidInboxEmail")}</span>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <EmailRow
          unread
          fresh
          sender={t("orbidEmailSenderOrbitra")}
          subject={t("orbidEmailCredentialSubject")}
          preview={t("orbidEmailCredentialPreview")}
          ts={t("orbidEmailTsJustNow")}
        />
        {showProposalEmail && (
          <EmailRow
            unread
            fresh
            sender={t("orbidEmailSenderOrbitra")}
            subject={t("orbidEmailProposalSubject")}
            preview={t("orbidEmailProposalPreview")}
            ts={t("orbidEmailTsJustNow")}
          />
        )}
      </div>
    </div>
  );
}

function EmailRow({
  sender, subject, preview, ts, unread, fresh,
}: {
  sender: string;
  subject: string;
  preview: string;
  ts: string;
  unread?: boolean;
  fresh?: boolean;
}) {
  return (
    <div
      className={clsx(
        "rounded-md border px-3 py-2 transition-state",
        unread
          ? "border-state-verified/40 bg-state-verified/8"
          : "border-orbitra-muted/20 bg-orbitra-ink/30",
        fresh && "shadow-[0_0_14px_var(--state-verified-glow)]",
      )}
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-[11px] font-semibold text-orbitra-paper">
          {unread && <span className="inline-block h-1.5 w-1.5 rounded-full bg-state-verified" />}
          {sender}
        </span>
        <span className="font-mono text-[9px] text-orbitra-muted">{ts}</span>
      </div>
      <p className="mt-1 text-[11px] font-semibold text-orbitra-paper">{subject}</p>
      <p className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-orbitra-teal-soft">{preview}</p>
    </div>
  );
}
